import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import InfiniteScroll from 'react-infinite-scroll-component';

import { GridWidget } from '../types';
import { useSort } from '../../../hooks/useSort';
import { Column } from '../../NewStrategy/StrategyTable';
import { Sort } from '../../NewStrategy/Sort';
import { NftColumn } from './NftColumn';
import CollectionDetails from './CollectionDetails';
import { GET_NFT_COLLECTIONS_BY_RANKS } from '../../../http/apollo-queries';
import { NFTCollection } from './types';
import { Loading } from '../../UI';
import { NFTAddress, NFTBlockchain, NFTDiff, NFTspanWithIcon } from './Utils';

import './nft-explorer.css';
import { useErrorHandler } from 'react-error-boundary';

interface NFTExplorerProps extends GridWidget {}

const COLUMNS_NFT_EXPLORER: {
  t: string;
  fixed?: boolean;
}[] = [
  { t: 'COLLECTION', fixed: true },
  { t: 'BLOCKCHAIN' },
  { t: 'FLOOR PRICE' },
  { t: 'ATH' },
  { t: 'AVG.PRICE(7D)', fixed: true },
  { t: 'VOLUME(7D)', fixed: true },
  { t: 'LAST SALE' },
  { t: 'CREATION DATE' },
  { t: 'OWNERS' },
  { t: 'SUPPLY' },
  { t: 'ADDRESS' },
];

export const NFT_EXPLORER_COLS = COLUMNS_NFT_EXPLORER.map((col) => col.t);

export const NFTExplorer = ({
  width,
  setLastWidgetUpdateAt,
}: NFTExplorerProps) => {
  const onError = useErrorHandler();

  const {
    data: {
      getNftCollectionsByRanks: { items = [], nextToken = null } = {},
    } = {},
    loading,
    fetchMore,
  } = useQuery<{
    getNftCollectionsByRanks: { items: NFTCollection[]; nextToken: string };
  }>(GET_NFT_COLLECTIONS_BY_RANKS, {
    variables: {
      limit: 20,
    },
    onError,
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const data = [...items] || [];

  const { type, getSortValue, handleSort } = useSort([
    'placeholder', // placeholder - so sort won't happen automatically when new data arrives
    ...COLUMNS_NFT_EXPLORER.map((colum) => colum.t),
  ]);

  if (type === 'COLLECTION') {
    data.sort((a, b) => getSortValue(a.name, b.name));
  } else if (type === 'FLOOR PRICE') {
    data.sort((a, b) => getSortValue(a.floorAskPrice, b.floorAskPrice));
  } else if (type === 'AVG.PRICE(7D)') {
    data.sort((a, b) =>
      getSortValue(a.floorSale.sevenDay, b.floorSale.sevenDay)
    );
  } else if (type === 'VOLUME(7D)') {
    data.sort((a, b) => getSortValue(a.volume.sevenDay, b.volume.sevenDay));
  }

  const [nftCollectionDetails, setNftCollectionDetails] =
    useState<null | JSX.Element>(null);

  function handleClickCollection(collectionId: string) {
    const selectedCollection = data.find((el) => el.address === collectionId);

    selectedCollection &&
      setNftCollectionDetails(
        <CollectionDetails
          collectionData={selectedCollection}
          goBack={() => setNftCollectionDetails(null)}
        />
      );
  }

  let header = COLUMNS_NFT_EXPLORER.filter((col) =>
    width === 1 ? col.fixed : true
  )
    // .map((col) => col.t)
    .map(({ t: columnName }, position) => (
      <NftColumn width={width} position={position}>
        <Column
          className={`flex items-center w-full h-full border-grey-10 dark:bg-grey-90`}
          element={
            <Sort
              className={`
                flex
                items-center
                text-sm
                ${!position && 'pl-3'}
                font-medium
                w-full
                h-full
              `}
              columnName={columnName}
              key={columnName}
              type={type}
              handleSort={handleSort}
            />
          }
          span={1}
        />
      </NftColumn>
    ));

  let collections = data.map((coll, position) => {
    const { address, volume, name, floorAskPrice, floorSale, imageUrl } = coll;

    const COLLECTION = (
      <>
        <div
          className={`
            flex
            items-center
            border-l
            border-grey-10
            dark:border-grey-70
            pl-3
            h-full
            w-full
            dark:bg-grey-90
          `}
        >
          <div className="flex w-full items-center">
            <img
              src={imageUrl}
              alt={'nft logo'}
              className={'w-9 h-9 rounded-full object-cover'}
            />
            <p
              className={`text-base font-medium ml-3 truncate ${
                width === 1 && 'w-1/2'
              } `}
            >
              {name}
            </p>
          </div>
        </div>
      </>
    );
    const AVG_PRICE = (
      <NFTDiff start={floorSale.oneDay} end={floorSale.sevenDay} />
    );
    const VOLUME = <NFTDiff start={volume.oneDay} end={volume.sevenDay} />;
    const blockchain = address.startsWith('0x') ? 'Ethereum' : 'Solana';

    return (
      width === 1
        ? [COLLECTION, AVG_PRICE, VOLUME]
        : [
            // 'COLLECTION'
            COLLECTION,
            // 'BLOCKCHAIN',
            <NFTBlockchain />,
            // 'FLOOR PRICE'
            <NFTspanWithIcon withBorder>{floorAskPrice}</NFTspanWithIcon>,
            // 'ATH'
            <div
              className={`
                flex
                border-l
                border-grey-10
                dark:border-grey-70
                w-full
                h-full
                dark:bg-grey-90
                dark:text-white
              `}
            >
              <span className="m-auto">-</span>
            </div>,
            // 'AVG.PRICE(7D)'
            AVG_PRICE,
            // 'VOLUME(7D)'
            VOLUME,
            // 'LAST SALE'
            <div
              className={`
              flex
              border-l
              border-grey-10
              dark:border-grey-70
              w-full
              h-full
              dark:bg-grey-90
              dark:text-white
            `}
            >
              <span className="m-auto">-</span>
            </div>,
            // 'CREATION DATE'
            <div
              className={`
                flex
                border-l
                border-grey-10
                dark:border-grey-70
                w-full
                h-full
                dark:bg-grey-90
                dark:text-white
              `}
            >
              <span className="m-auto">-</span>
            </div>,
            // 'OWNERS'
            <div
              className={`
                flex
                border-l
                border-grey-10
                dark:border-grey-70
                w-full
                h-full
                dark:bg-grey-90
                dark:text-white
              `}
            >
              <span className="m-auto">-</span>
            </div>,
            // 'SUPPLY'
            <div
              className={`
                flex
                border-l
                border-grey-10
                dark:border-grey-70
                w-full
                h-full
                dark:bg-grey-90
                dark:text-white
              `}
            >
              <span className="m-auto">-</span>
            </div>,
            // 'ADDRESS'
            <NFTAddress address={address} blockchain={blockchain}>
              {address}
            </NFTAddress>,
          ]
    ).map((element, position) => (
      <NftColumn
        width={width}
        position={position}
        key={`collection_${address}`}
        className={`
          ${!position && width > 1 && 'border-r'}
          h-15
        `}
      >
        {element}
      </NftColumn>
    ));
  });

  const table = [header, ...collections];

  if (loading)
    return (
      <div className={'w-full h-full flex justify-center'}>
        <Loading status={true} size={'large'} />
      </div>
    );

  if (nftCollectionDetails) return nftCollectionDetails;

  return (
    <div className={'h-full w-full overflow-hidden'}>
      <div id={'NFT-exp-scrollableDiv'} className={'h-full overflow-auto'}>
        <InfiniteScroll
          next={() => {
            return fetchMore({
              variables: {
                nextToken,
              },
              updateQuery(prev, newCollections) {
                return Object.assign({}, prev, {
                  getNftCollectionsByRanks: {
                    nextToken:
                      newCollections?.fetchMoreResult?.getNftCollectionsByRanks
                        .nextToken || null,
                    items: [
                      ...prev.getNftCollectionsByRanks.items,
                      ...(newCollections?.fetchMoreResult
                        ?.getNftCollectionsByRanks.items || []),
                    ],
                  },
                });
              },
            });
          }}
          hasMore={!!nextToken}
          loader={
            <div className={'w-full flex justify-center'}>
              <Loading status={true} size={'large'} />
            </div>
          }
          dataLength={data.length}
          scrollableTarget={'NFT-exp-scrollableDiv'}
        >
          {table.map((arr, i) => {
            const isCollection =
              arr.length > 0 &&
              arr[0].key !== null &&
              arr[0].key.toString().includes('collection_');

            return (
              <div
                key={'nft-explorer-table-th-' + i}
                className={`
                  flex
                  ${!i && 'sticky top-0 z-10'}
                  ${
                    width === 1 &&
                    isCollection &&
                    'hover:border hover:border-grey-50'
                  }
                `}
                onClick={() => {
                  if (width !== 1 || !isCollection) return;
                  handleClickCollection(arr[0].key!.toString().slice(11));
                }}
              >
                {arr.map((el, j) => (
                  <div
                    key={'nft-explorer-tr-' + j}
                    className={`flex w-full ${!j && 'sticky left-0'}`}
                  >
                    {el}
                  </div>
                ))}
              </div>
            );
          })}
        </InfiniteScroll>
      </div>
    </div>
  );
};
